import { comScoreFactory, analyticsTrack } from '@sky-it-livedata-libraries/livedata-commons';
import debounce from 'lodash.debounce';

import { advOutbrainShowHide, advRefresh } from './adv-utils';
import { dynamicParselyTrackPageView } from './parsely-tracking';
import { trackIntcmp } from './intcmp-tracking';
import { calcThreshold } from './intersect';
import {
  CSS_PREFIX,
  ACTIVE_TAB_CLASS_PREFIX,
  TABS_MENU_CLASSNAME,
  TAB_SWITCH_CONTENT_ID,
  HERO_SLIM_HOOK,
  LOCAL_NAV_EL_SELECTOR,
  SECTION_NAV_EL_SELECTOR,
} from './constants';
import { tabScrollIntoView } from './tab-scroll-into-view';
import { getActiveTab } from './get-active-tab';
import initCarousel from './related-players-carousel';

const comscoreTrack = comScoreFactory();

const isDriverPage = Boolean(document.querySelector(`.${CSS_PREFIX}driver-page-hero__wrapper`)) && 'driverpage';

const trackedTabs = new Set<string>();
const tabsMap = new Map<string, HTMLElement>();

const trackDigitalData = (tabName: string) => {
  const digitalData = (window as any).digitalData;

  let pageName = digitalData?.page?.pageInfo?.pageName || '';
  const pageNameSplit = pageName.split(':');
  pageNameSplit[5] = tabName;
  pageName = pageNameSplit.join(':');
  const intcmp = `skysport_${isDriverPage ? 'driverpage' : 'playerpage'}_${tabName}_tab`;

  const digitalDataUpdate = {
    ...digitalData,
    page: {
      ...(digitalData?.page ?? {}),
      pageInfo: {
        ...(digitalData?.page?.pageInfo ?? {}),
        pageName,
        intcmp,
      },
    },
  };

  (window as any).digitalData = digitalDataUpdate;
};

const trackTab = (id: string) => {
  if (!tabsMap.has(id)) {
    return;
  }

  try {
    const tabToTrack = tabsMap.get(id) as HTMLElement;
    const trackingData = tabToTrack.dataset.tracking || '';
    const tabContent =
      tabToTrack.querySelector('span.ftbl__tab-item__label')?.innerHTML?.toLocaleLowerCase().replace(' ', '-') || '';
    dynamicParselyTrackPageView({ url: `${location.origin}#${tabContent}`, urlRef: location.href });
    trackDigitalData(tabContent);
    trackIntcmp(tabContent, isDriverPage ? 'DP' : 'PP');
    analyticsTrack('track_tab', trackingData)
      .then((value) => console.log('[analytics-track.ts]', 'track_tab', value)) // eslint-disable-line no-console
      .catch((err) => console.warn('[analytics-track.ts]', 'track_tab', err)); // eslint-disable-line no-console
    comscoreTrack(id);

    /**
     * As of today, this function causes an error on the page due to _4SDexRefresh,
     * so make sure to keep it at the end.
     */
    advRefresh();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`error tracking tab ${id}`, e);
  }

  trackedTabs.add(id);
};

const trackTabDb = debounce(trackTab, 1000);

const manageIntersectionEntry = (entries: IntersectionObserverEntry[], menu: HTMLElement): void => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      if (!trackedTabs.has(entry.target.id)) {
        trackTabDb(entry.target.id);
      }
      if (tabsMap.has(entry.target.id)) {
        const index = entry.target.id.replace(/[^\d+]/g, '');
        menu.dataset.state = `${ACTIVE_TAB_CLASS_PREFIX}${index}`;
        tabScrollIntoView(tabsMap.get(entry.target.id)!);
      }
    }
  });
};

const startObserver = (menu: HTMLElement) => {
  // const targets = document.querySelectorAll('.ftbl__tabs-switch__content');
  const targets = document.querySelectorAll(`.${CSS_PREFIX}${TAB_SWITCH_CONTENT_ID}`);
  const observer = new IntersectionObserver(
    (entries, observer) =>
      window.requestIdleCallback
        ? window.requestIdleCallback(() => manageIntersectionEntry(entries, menu))
        : manageIntersectionEntry(entries, menu),
    { threshold: calcThreshold(targets, 0.75, 250), rootMargin: '0px 0px 0px 0px' }
  );
  targets.forEach((target) => observer.observe(target));
};

// TODO: refactor using anchorTabOnTop() returning a value, not performing a scroll() action
const scrollToTabContent = (tabContent: HTMLElement) => {
  // Compute ALL STICKY ELEMENTS height for exacts scrolling
  // GLOBAL NAV and HERO FULL are excudedf (not sticky)

  const heightNavLocal: number =
    Number(document.querySelector(LOCAL_NAV_EL_SELECTOR)?.getBoundingClientRect().height) ?? 67;

  const heightNavSection: number =
    Number(document.querySelector(SECTION_NAV_EL_SELECTOR)?.getBoundingClientRect().height) ?? 61;

  const heightHeroSlim: number = document.querySelector(HERO_SLIM_HOOK)!.getBoundingClientRect().height ?? 60;

  const titleHeight = tabContent.querySelector('[data-attribute]')?.getBoundingClientRect().height ?? 38;

  const topTabContent: number = tabContent.getBoundingClientRect().top;
  const offset = heightNavLocal + heightNavSection + heightHeroSlim + titleHeight;
  const top: number = topTabContent + window.scrollY - offset || 0;
  window.scrollTo({ top, behavior: 'smooth' });
};

const tabAddClickListener = (menu: Element, tab: HTMLElement): void => {
  const target = tab.dataset?.url?.replace('#', '') ?? tab.id;
  tabsMap.set(target, tab);
  const tabContent: HTMLElement | null = document.getElementById(target);

  if (tabContent?.children.length) {
    tab.addEventListener('click', (e) => {
      tabScrollIntoView(tab);
      scrollToTabContent(tabContent);
    });
  }
};

/**
 * Hydrate Player and Driver pages main menu and Tabs
 */
const resumeAthletePages = (): void => {
  const menu: HTMLElement | null = document.querySelector(`.${CSS_PREFIX}${TABS_MENU_CLASSNAME}.pp-menu-tabs`);

  if (menu) {
    const menuItems: HTMLCollection = menu.children;
    startObserver(menu);
    Array.from(menuItems).forEach((tab: HTMLElement) => tabAddClickListener(menu, tab));
    advOutbrainShowHide(getActiveTab());
  }

  initCarousel();
};

export default resumeAthletePages;
